import { useTranslation } from "next-i18next";
import { Pagination, PaginationProps, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { formatUSD, truncateToFullChar } from "@/utils";
import { useEffect, useState } from "react";
import { getDonationLeaderBoard } from "@/api/donation";
import { ILeadboardItem } from "@/props/IDonation";
import MobileRankRow from "./MobileRankRow";
import Image from "next/image";
import { useAppSelector } from "@/store/hooks";

const Leaderboard = ({ isMobile }: { isMobile: boolean }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [tableData, setTableData] = useState<ILeadboardItem[]>([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 5,
  });
  const userInfo = useAppSelector((state) => state.user.userInfo);
  const uuid = userInfo?.uuid;
  useEffect(() => {
    getLeaderBoard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  // table columns
  const tableColumns: ColumnsType<ILeadboardItem> = [
    {
      dataIndex: "index",
      key: "index",
      render: (_, record, index) => {
        const globalRank = (queryParams.page - 1) * queryParams.limit + index + 1;
        return isMobile ? (
          <MobileRankRow globalRank={globalRank} record={record} t={t} uuid={uuid} />
        ) : (
          <div className="grid items-center gap-x-8 text-white [grid-template-columns:4fr_4fr_4fr_3fr]">
            <div className="flex items-center gap-4">
              {globalRank <= 3 ? (
                <div className="relative left-2 mr-2 h-[48px] w-[38px]">
                  <Image
                    src={`/images/csr/rank_${globalRank}.webp`}
                    alt={`Rank ${globalRank} badge`}
                    width={38}
                    height={48}
                    className="object-contain"
                  />
                </div>
              ) : (
                <span className="w-12 text-center text-3xl font-semibold text-[#ffc107]">{globalRank}</span>
              )}
              <span
                className="overflow-hidden whitespace-nowrap text-2xl font-semibold"
                title={record.name}
              >
                {truncateToFullChar(record.name, 16)}
              </span>
              {uuid === record.uuid && (
                <span className="w-10 rounded-xl bg-[#efff04] py-[2px] text-center text-sm text-stone-800">
                  {t("csr.current_user")}
                </span>
              )}
            </div>

            <div className="flex items-center justify-start gap-2">
              <span className="min-w-[72px] text-base text-[#9ca3af]">{t("csr.ib_investment_donation")}</span>
              <span className="text-2xl">{formatUSD(record?.sumTotalTradingDonationAmountUsd)} USD</span>
            </div>

            <div className="flex items-center justify-start gap-2">
              <span className="min-w-[72px] text-base text-[#9ca3af]">{t("csr.ib_dream_donation")}</span>
              <span className="text-2xl">{formatUSD(record?.sumTotalDreamPointsDonationAmountUsd)} USD</span>
            </div>

            <div className="flex items-center justify-end gap-2">
              <span className="min-w-[72px] text-base text-[#9ca3af]">{t("csr.donation_total")}</span>
              <span className="text-2xl">{formatUSD(record?.sumTotalDonationAmountUsd)} USD</span>
            </div>
          </div>
        );
      },
    },
  ];

  const onPageChange: PaginationProps["onChange"] = (page, pageSize) => {
    const data = JSON.parse(JSON.stringify(queryParams));
    data.page = page;
    data.limit = pageSize;
    setQueryParams(data);
  };
  const getLeaderBoard = async () => {
    setLoading(true);
    try {
      const data: any = await getDonationLeaderBoard(queryParams);
      setTableData(data.list);
      setTotal(data.total >= 50 ? 50 : data.total);
      setLoading(false);
    } catch (errorInfo) {
      console.log("Failed:", errorInfo);
      setLoading(false);
    }
  };
  return (
    <div className="relative text-white">
      <Image
        src="/images/csr/leaderboard_bg.webp"
        alt="Leaderboard background"
        fill
        className="-z-10 object-cover object-center"
      />
      <div className={`m-auto max-w-[1440px] text-center ${isMobile ? "my-10" : "my-20"}`}>
        <h3 className={`mb-4 font-semibold ${isMobile ? "h-[32px] text-center text-2xl" : "h-[60px] text-5xl"}`}>
          {t("csr.leaderboard")}
        </h3>
        <p className={`text-gray-300 ${isMobile ? "mb-[28px] text-sm" : "mb-8 text-2xl"}`}> {t("csr.top_50")}</p>
        <div
          className={`overflow-hidden rounded-xl bg-gradient-to-b from-[#ffffff00] to-[#fff4f419] ${
            isMobile ? "mx-3 border-t border-[#444346]" : ""
          }`}
        >
          <Table
            showHeader={false}
            dataSource={tableData}
            columns={tableColumns}
            pagination={false}
            loading={loading}
            rowKey="uuid"
            className="custom-ranking-table"
            rowClassName={(record) => (record.uuid === uuid ? "current-user-row" : "")}
          />
        </div>
        <p className="mt-4 flex items-center justify-center text-base">
          <span className="mr-2 mt-1 text-2xl text-primary">*</span>
          {t("csr.leaderboard_update_time")}
        </p>
        <div className="mt-6 flex justify-center [&_.ant-pagination-item_a]:!text-[#e5e7eb]">
          <Pagination
            current={queryParams.page}
            total={total}
            pageSize={5}
            onChange={onPageChange}
            showSizeChanger={false}
            className="
            [&_.ant-pagination-disabled_.ant-pagination-item-link]:!text-gray-400
            [&_.ant-pagination-item-active]:!border-none
            [&_.ant-pagination-item-active]:!bg-[#747477]
            [&_.ant-pagination-item-active_a]:!text-[#e5e7eb]
            [&_.ant-pagination-item-link]:!border-none

            [&_.ant-pagination-item-link]:!bg-transparent
            [&_.ant-pagination-item-link]:!text-[#e5e7eb]
            [&_.ant-pagination-item:hover]:!bg-[#747477]


            [&_.ant-pagination-item:hover_a]:!text-[#e5e7eb]
            [&_.ant-pagination-item]:!border-none
            [&_.ant-pagination-item]:bg-[#35363b]

            [&_.ant-pagination-item_a]:!text-[#e5e7eb]

            [&_.ant-pagination-jump-next_.ant-pagination-item-ellipsis]:!text-[#e5e7eb]
            [&_.ant-pagination-jump-next_.anticon]:!text-[#e5e7eb]
            [&_.ant-pagination-jump-prev_.ant-pagination-item-ellipsis]:!text-[#e5e7eb]
            [&_.ant-pagination-jump-prev_.anticon]:!text-[#e5e7eb]

            [&_.ant-pagination-next]:!border-none
            [&_.ant-pagination-next]:!bg-transparent
            [&_.ant-pagination-prev]:!border-none
            [&_.ant-pagination-prev]:!bg-transparent"
          />
        </div>
      </div>
      <style jsx global>
        {`
          .custom-ranking-table .ant-table {
            background-color: transparent !important;
          }
          .custom-ranking-table .ant-table-container {
            background-color: transparent !important;
          }
          .custom-ranking-table .ant-table-tbody > tr:hover > td {
            background-color: transparent !important;
            transition: none;
          }
          .custom-ranking-table .ant-table-tbody > tr > td {
            border-bottom: 1px solid #444346;
            height: 95px;
            padding: 0 16px;
            background-color: transparent;
            color: #fff !important;
          }
          .custom-ranking-table .ant-table-tbody > tr.current-user-row > td {
            background-color: #555559 !important;
          }
          .custom-ranking-table .ant-table-tbody > tr:hover > td {
            background-color: transparent;
          }
          .custom-ranking-table .ant-table {
            background: transparent;
          }
          .custom-ranking-table .ant-table-container::before {
            display: none;
          }
          .ant-pagination-item-active {
            background-color: #747477 !important;
          }
          .custom-ranking-table .ant-table-placeholder {
            background-color: transparent !important;
          }
          .custom-ranking-table .ant-table-placeholder .ant-empty {
            color: #e5e7eb;
          }
        `}
      </style>
    </div>
  );
};

export default Leaderboard;
